import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { buildListOptions } from "./filterOptions";
import { normalizeText } from "./filterFns";
import { useDismiss } from "./useDismiss";
import { TOOLBAR_BUTTON } from "./styles";

export const MAX_LIST_OPTIONS = 200;
const PANEL_WIDTH = 288;
const INPUT = "w-full rounded border border-primary/40 bg-mgs-surface px-2 py-1 text-laranjinha outline-none focus:border-primary";

const FilterActions = ({ onClear, onApply, applyDisabled = false }) => (
    <div className="mt-3 flex justify-between gap-2">
        <button type="button" className={TOOLBAR_BUTTON} onClick={onClear}>Limpar</button>
        <button type="button" className="rounded bg-primary px-3 py-1 text-sm font-semibold text-black hover:bg-[#b47c06] disabled:opacity-50" onClick={onApply} disabled={applyDisabled}>Aplicar</button>
    </div>
);

const ListFilter = ({ column, config, onClose }) => {
    const current = column.getFilterValue();
    const facets = column.getFacetedUniqueValues();
    const options = useMemo(() => buildListOptions(facets, config, current), [facets, config, current]);
    const [selected, setSelected] = useState(() => new Set(current ?? options.map((option) => option.key)));
    const [search, setSearch] = useState("");
    const matching = options.filter((option) => normalizeText(option.label).includes(normalizeText(search)));
    const shown = matching.slice(0, MAX_LIST_OPTIONS);
    const scope = search ? "visíveis" : "todos";

    const toggle = (key) => setSelected((previous) => {
        const next = new Set(previous);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const markMatching = () => setSelected((previous) => new Set([...previous, ...matching.map((option) => option.key)]));
    const unmarkMatching = () => setSelected((previous) => {
        const next = new Set(previous);
        matching.forEach((option) => next.delete(option.key));
        return next;
    });
    const apply = () => {
        const keys = options.map((option) => option.key).filter((key) => selected.has(key));
        column.setFilterValue(keys.length === options.length ? undefined : keys);
        onClose();
    };

    return (
        <div>
            <input type="search" className={INPUT} placeholder="Buscar valor…" value={search} onChange={(event) => setSearch(event.target.value)} autoFocus />
            <div className="my-2 flex gap-3 text-xs">
                <button type="button" className="underline hover:text-primary" onClick={markMatching}>{`Marcar ${scope}`}</button>
                <button type="button" className="underline hover:text-primary" onClick={unmarkMatching}>{`Desmarcar ${scope}`}</button>
            </div>
            <ul className="max-h-60 overflow-y-auto">
                {shown.map((option) => (
                    <li key={option.key}>
                        <label className="flex items-center gap-2 py-0.5">
                            <input type="checkbox" checked={selected.has(option.key)} onChange={() => toggle(option.key)} />
                            <span className="flex-1 truncate">{option.label}</span>
                            <span className="text-xs text-laranjinha/50">{option.count}</span>
                        </label>
                    </li>
                ))}
            </ul>
            {matching.length > shown.length ? (
                <p className="mt-1 text-xs text-laranjinha/50">{`Mostrando ${shown.length} de ${matching.length}; refine a busca.`}</p>
            ) : null}
            <FilterActions onClear={() => { column.setFilterValue(undefined); onClose(); }} onApply={apply} applyDisabled={selected.size === 0} />
        </div>
    );
};

const DateFilter = ({ column, onClose }) => {
    const current = column.getFilterValue() ?? {};
    const [from, setFrom] = useState(current.from ?? "");
    const [to, setTo] = useState(current.to ?? "");
    return (
        <div className="flex flex-col gap-2">
            <label className="flex items-center justify-between gap-2">De<input type="date" className={INPUT} value={from} onChange={(event) => setFrom(event.target.value)} /></label>
            <label className="flex items-center justify-between gap-2">Até<input type="date" className={INPUT} value={to} onChange={(event) => setTo(event.target.value)} /></label>
            <FilterActions
                onClear={() => { column.setFilterValue(undefined); onClose(); }}
                onApply={() => { column.setFilterValue(from || to ? { from, to } : undefined); onClose(); }}
            />
        </div>
    );
};

const TextFilter = ({ column, onClose }) => {
    const [text, setText] = useState(column.getFilterValue() ?? "");
    const apply = () => { column.setFilterValue(text.trim() ? text.trim() : undefined); onClose(); };
    return (
        <form onSubmit={(event) => { event.preventDefault(); apply(); }}>
            <input type="text" className={INPUT} placeholder="Contém…" value={text} onChange={(event) => setText(event.target.value)} autoFocus />
            <FilterActions onClear={() => { column.setFilterValue(undefined); onClose(); }} onApply={apply} />
        </form>
    );
};

const ColumnFilterPopover = ({ column, anchorRef, onClose }) => {
    const config = column.columnDef.meta?.config ?? {};
    const panelRef = useRef(null);
    useDismiss(true, [panelRef, anchorRef], onClose);
    const rect = anchorRef?.current?.getBoundingClientRect?.();
    const style = rect
        ? { top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)) }
        : { top: 80, left: 8 };
    const title = config.header || column.id;

    return createPortal(
        <div ref={panelRef} role="dialog" aria-label={`Filtrar ${title}`} className="fixed z-50 w-72 rounded border border-primary/40 bg-mgs-elevated p-3 text-left text-sm normal-case text-laranjinha shadow-xl" style={style}>
            <p className="mb-2 font-semibold">{title}</p>
            {config.filter === "list" ? <ListFilter column={column} config={config} onClose={onClose} /> : null}
            {config.filter === "date" ? <DateFilter column={column} onClose={onClose} /> : null}
            {config.filter === "text" || config.filter === "digits" ? <TextFilter column={column} onClose={onClose} /> : null}
        </div>,
        document.body
    );
};

export default ColumnFilterPopover;
